import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { usePageTitle } from "@/contexts/PageTitleContext.tsx"
import { useEffect, useState } from "react"
import { PitchCard } from "@/components/PitchCard.tsx"
import { useQuery } from "@tanstack/react-query"
import { pitchesApi } from "@/api/pitches.api.ts"
import { Card, CardContent, CardHeader } from "@/components/ui/card.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "@/components/ui/item.tsx"
import { FaceNeutral, Filter } from "lucide-react"
import { CreatePitchButton } from "@/components/CreatePitchButton.tsx"
import { ErrorCard } from "@/components/ErrorCard.tsx"
import { AppPagination } from "@/components/AppPagination.tsx"
import { useTranslation } from "react-i18next"
import type { Status } from "@/components/StatusBadge.tsx"
import { StatusFilter } from "@/components/StatusFilter.tsx"

export const Route = createFileRoute("/_protected/pitches")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Number(search.page ?? 1),
    status: search.status,
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const { setTitle } = usePageTitle()
  const { page, status } = useSearch({ from: "/_protected/pitches" })
  const skipCount = (page - 1) * 10
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [pitches, setPitches] = useState<Array<any>>([])

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["pitches/getAll", skipCount, status],
    queryFn: () => pitchesApi.getAll({ skipCount, status }),
    structuralSharing: false,
  })

  useEffect(() => {
    setTitle(t("sidebar.pitches"))
    if (data?.result?.totalCount > 0) {
      setTitle(`${t("sidebar.pitches")} (${data?.result?.totalCount})`)
    }
  }, [setTitle, data])

  useEffect(() => {
    if (data && data.result) {
      setPitches(data.result.items)
    }
  }, [data, page, status])

  if (isLoading || isFetching) {
    return (
      <div className="flex w-full flex-wrap justify-between pb-3">
        <div className="flex-[100%] !cursor-pointer p-3 pr-0 pl-0 md:flex-1/2 md:nth-of-type-[2n]:pl-3 md:nth-of-type-[2n+1]:pr-3">
          <Card className="w-full">
            <CardHeader>
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="mt-4 h-4 w-1/2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-6 w-full" />
              <Skeleton className="mt-4 h-6 w-full" />
            </CardContent>
          </Card>
        </div>
        <div className="flex-[100%] !cursor-pointer p-3 pr-0 pl-0 md:flex-1/2 md:nth-of-type-[2n]:pl-3 md:nth-of-type-[2n+1]:pr-3">
          <Card className="w-full">
            <CardHeader>
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-6 w-full" />
              <Skeleton className="mt-4 h-6 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (error) {
    return <ErrorCard isRefresh />
  }

  return (
    <div className="flex w-full flex-wrap justify-between pb-3">
      <Item className="mb-4" variant="muted">
        <ItemContent className="flex flex-row items-center justify-start gap-3">
          <Filter />
          <StatusFilter
            value={status as Status | undefined}
            onChange={(status) => {
              navigate({
                search: (prev) => ({
                  ...prev,
                  status: status || undefined,
                  page: 1,
                }),
              })
            }}
          />
        </ItemContent>
      </Item>
      {pitches.length === 0 && (
        <Item
          variant="muted"
          className="flex flex-col items-center justify-center gap-3"
        >
          <ItemContent className="mt-8 flex items-center gap-4 text-center">
            <FaceNeutral size={48} className="text-secondary" />
            <ItemTitle className="font-heading text-xl">
              Sorry! You don't have any pitches yet!
            </ItemTitle>
            <ItemDescription>
              Create pitches to get your story published!
            </ItemDescription>
          </ItemContent>
          <ItemActions className="m-8">
            <CreatePitchButton />
          </ItemActions>
        </Item>
      )}
      {pitches.length > 0 && (
        <>
          {pitches.map((pitch) => (
            <div
              key={pitch.publicId}
              className="w-full !cursor-pointer p-3 ps-0 pe-0 md:flex-[0_0_50%] md:[&:nth-of-type(2n)]:pe-3 md:[&:nth-of-type(2n+1)]:ps-3"
            >
              <PitchCard pitch={pitch} />
            </div>
          ))}
        </>
      )}
      <div className="flex w-full justify-end">
        <AppPagination
          currentPage={page}
          totalCount={data?.result?.totalCount ?? 0}
          onPageChange={(page) => {
            navigate({
              search: (prev) => ({
                ...prev,
                page,
              }),
            })
          }}
        />
      </div>
    </div>
  )
}
